/**
 * Quick Add Icon - Modern Add to Cart functionality
 * Handles click events for the floating add to cart icons on product images
 */

class QuickAddIcon {
  constructor() {
    this.init();
  }

  init() {
    // Bind click events to all quick add icons
    document.addEventListener('click', this.handleIconClick.bind(this));
  }

  handleIconClick(event) {
    const icon = event.target.closest('.product-card__quick-icon');
    if (!icon || icon.matches('a[href]')) return;

    event.preventDefault();
    event.stopPropagation();

    const productId = icon.dataset.productId;
    const variantId = icon.dataset.variantId;

    if (!productId || !variantId) {
      console.error('Product ID or Variant ID missing');
      return;
    }

    // Check if product drawer is enabled and available
    const drawerEnabled = window.theme?.settings?.product_drawer_enable;
    const drawer = document.querySelector('quick-cart-drawer');

    if (drawerEnabled && drawer) {
      // Open product drawer instead of adding to cart directly
      const productHandle = icon.dataset.productHandle;

      if (productHandle) {
        drawer.open(productHandle, variantId);
        return;
      }
    }

    // Fallback to direct add to cart if drawer is disabled or unavailable
    this.addToCart(icon, variantId);
  }

  async addToCart(icon, variantId) {
    // Show loading state
    this.setLoadingState(icon, true);

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Show success feedback
      this.showSuccessFeedback(icon);

      // Update cart if there's a cart component
      this.updateCartDisplay();

      // Dispatch custom event for other components
      document.dispatchEvent(
        new CustomEvent('cart:item-added', {
          detail: { variantId, result },
        })
      );
    } catch (error) {
      console.error('Add to cart failed:', error);
      this.showErrorFeedback(icon);
    } finally {
      // Remove loading state after delay
      setTimeout(() => {
        this.setLoadingState(icon, false);
      }, 1500);
    }
  }

  setLoadingState(icon, loading) {
    if (loading) {
      icon.classList.add('loading');
      icon.setAttribute('disabled', 'true');
      icon.style.pointerEvents = 'none';
    } else {
      icon.classList.remove('loading', 'success', 'error');
      icon.removeAttribute('disabled');
      icon.style.pointerEvents = 'auto';
    }
  }

  showSuccessFeedback(icon) {
    icon.classList.remove('loading');
    icon.classList.add('success');

    // Reset after 1.5 seconds
    setTimeout(() => {
      icon.classList.remove('success');
    }, 1500);
  }

  showErrorFeedback(icon) {
    icon.classList.remove('loading');
    icon.classList.add('error');

    // Add shake animation
    icon.style.animation = 'quickIconShake 0.5s ease-in-out';

    setTimeout(() => {
      icon.classList.remove('error');
      icon.style.animation = '';
    }, 1500);
  }

  updateCartDisplay() {
    // Try to update cart drawer if it exists
    if (window.theme && window.theme.cart) {
      window.theme.cart.getCart();
    }

    // Try to update cart count if element exists
    const cartCount = document.querySelector('.cart-count');
    if (cartCount) {
      fetch('/cart.js')
        .then((response) => response.json())
        .then((cart) => {
          cartCount.textContent = cart.item_count;
        })
        .catch(console.error);
    }

    // Dispatch event for cart drawer
    if (document.querySelector('.cart-drawer')) {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
  }
}

// Add CSS for error shake animation
const style = document.createElement('style');
style.textContent = `
  @keyframes quickIconShake {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
    20%, 40%, 60%, 80% { transform: translateX(2px); }
  }
  
  .product-card__quick-icon.error {
    background-color: #ef4444 !important;
  }
`;

if (!document.head.querySelector('#quick-add-icon-styles')) {
  style.id = 'quick-add-icon-styles';
  document.head.appendChild(style);
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new QuickAddIcon());
} else {
  new QuickAddIcon();
}

// Re-initialize when new content is loaded (for AJAX)
document.addEventListener('shopify:section:load', () => new QuickAddIcon());
